import React, { useCallback, useRef, useLayoutEffect, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withFocus } from '@accedo/vdkweb-navigation';
import classNames from 'classnames';
import imgixUrl from '#/utils/imgix';
import { isWorkstation } from '#/utils/componentStyleConfig';
import leftArrow from '#/static/images/icons/carousel-left.svg';
import rightArrow from '#/static/images/icons/carousel-right.svg';
import styles from './swimlane.scss';
import {
  SWIMLANE_RIGHT_ARROW,
  SWIMLANE_LEFT_ARROW,
  imgixTileResizeParameters,
} from '#/config/constants';
import useIsVisible from '#/hooks/useIsVisible';
import {
  isItemOffScreenLeft,
  isItemOffScreenRight,
} from '../../utils/isItemOutsideScreen';

const renderItems = ({
  isLazyLoadingActive = false,
  shouldDisplayItems = false,
  items = [],
  emptyItems = [],
}) => {
  if (!isLazyLoadingActive || shouldDisplayItems) {
    return items;
  }

  return emptyItems;
};

const Swimlane = ({
  title,
  items,
  emptyItems = [],
  tileContainerClass,
  alwaysVisible = false,
  className,
  isTv,
  navId,
  setFocusChanged = () => {},
  separatorColor,
  backgroundColor,
  swimlaneSponsorData,
}) => {
  const totalOffset = useRef(0);
  const translationAmount = useRef(0);
  const [translateContainerRef, leftArrowRef, rightArrowRef] = [
    useRef(),
    useRef(),
    useRef(),
  ];
  const [isVisible, getRef, updateVisibility] = useIsVisible();
  const timerId = useRef(null);
  const shouldDisplayItems = alwaysVisible || isVisible;
  const isLazyLoadingActive = !isWorkstation();

  useLayoutEffect(() => {
    leftArrowRef.current.style.visibility = 'hidden';
    leftArrowRef.current.style.opacity = 0;
    if (translateContainerRef.current.offsetWidth < window.innerWidth) {
      rightArrowRef.current.style.visibility = 'hidden';
      rightArrowRef.current.style.opacity = 0;
    } else {
      rightArrowRef.current.style.visibility = 'visible';
      rightArrowRef.current.style.opacity = 1;
    }
    translationAmount.current =
      translateContainerRef.current.children[1]?.offsetLeft * 2;
  }, [translateContainerRef, leftArrowRef, rightArrowRef, items]);

  const onArrowClick = useCallback(
    moveRight => {
      if (
        moveRight &&
        translateContainerRef.current.offsetWidth + totalOffset.current >
          window.innerWidth
      ) {
        totalOffset.current -= translationAmount.current;
        if (
          translateContainerRef.current.offsetWidth + totalOffset.current <
          window.innerWidth
        ) {
          rightArrowRef.current.style.opacity = 0;
          rightArrowRef.current.style.visibility = 'hidden';
        }
        leftArrowRef.current.style.visibility = 'visible';
        leftArrowRef.current.style.opacity = 1;
      } else if (!moveRight && totalOffset.current < 0) {
        totalOffset.current += translationAmount.current;
        if (!totalOffset.current) {
          leftArrowRef.current.style.opacity = 0;
          leftArrowRef.current.style.visibility = 'hidden';
        }
        rightArrowRef.current.style.visibility = 'visible';
        rightArrowRef.current.style.opacity = 1;
      }
      translateContainerRef.current.style.transform = `translateX(${totalOffset.current}px)`;
    },
    [translateContainerRef, leftArrowRef, rightArrowRef],
  );

  const setUpdateTimer = () => {
    // TODO: this is a workaround for updating the hook
    // useIsVisible manually because sometimes it runs before
    // the element is scrolled back into the screen
    // One second delay is enough get the element into the screen
    if (timerId.current) {
      // clear here won't allow the update to happen
      // if you keep moving over the emptyItems
      return;
    }
    timerId.current = setTimeout(() => {
      updateVisibility();
      timerId.current = null;
    }, 1000);
  };

  useEffect(() => {
    return () => {
      // for timer cleanup
      if (timerId.current) {
        clearTimeout(timerId.current);
      }
    };
  }, []);

  const updateScroll = useCallback(item => {
    // Make sure we focus on what is focused (either to the left or to the right of the current position)
    // Code above relied on fake pressing the buttons that not always worked when we needed to scroll more
    // than one tile
    if (isItemOffScreenLeft(item)) {
      const currentItem = document.getElementById(item);
      totalOffset.current = currentItem.offsetLeft * -1;
      translateContainerRef.current.style.transform = `translateX(${totalOffset.current}px)`;
    } else if (isItemOffScreenRight(item)) {
      const currentItem = document.getElementById(item);
      const width = currentItem.parentElement.parentElement.clientWidth;
      totalOffset.current =
        width - (currentItem.offsetLeft + currentItem.offsetWidth * 2);
      translateContainerRef.current.style.transform = `translateX(${totalOffset.current}px)`;
    }

    // If it is not visible, it means you scrolled out of the screen (shouldn't) OR
    // that the visibility was calculated before the element goes back into screen.
    // In any case this should help to update the "useIsVisible" hook if the
    // second case occurs
    if (!shouldDisplayItems) {
      setUpdateTimer();
    }
  }, []);

  useEffect(() => {
    setFocusChanged(updateScroll);
  }, []);

  return (
    <div
      ref={getRef}
      id={navId}
      className={classNames(styles.container, className || '', {
        [styles.isTv]: isTv,
      })}
      style={
        swimlaneSponsorData
          ? {
              backgroundImage: `url(${imgixUrl(
                swimlaneSponsorData?.backgroundImageUrl,
                imgixTileResizeParameters.SWIMLANE,
              )})`,
              borderBottomColor: separatorColor || 'transparent',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              backgroundColor: backgroundColor || 'transparent',
            }
          : {
              borderBottomColor: separatorColor || 'transparent',
              backgroundColor: backgroundColor || 'transparent',
            }
      }
    >
      <div
        // onClick={onTitleClick || (() => {})}
        className={classNames({ [styles.titleWrapper]: title })}
      >
        <div className={styles.sponsoredContainer}>
          <p className={styles.title}>{title || ''}</p>
          {swimlaneSponsorData && (
            <div className={styles.sponsoredTile}>
              <p>{swimlaneSponsorData?.displaySubtext}</p>
            </div>
          )}
        </div>
        {swimlaneSponsorData && (
          <img
            className={styles.sponsoredImg}
            src={swimlaneSponsorData?.iconImageUrl}
            alt="Sponsered Tile"
          />
        )}
      </div>
      <div
        ref={translateContainerRef}
        className={classNames(styles.itemContainer, tileContainerClass || '')}
      >
        {renderItems({
          isLazyLoadingActive,
          shouldDisplayItems,
          items,
          emptyItems,
        })}
      </div>
      <div ref={leftArrowRef} className={styles.leftArrow}>
        <div
          id={`${SWIMLANE_LEFT_ARROW + navId}`}
          style={{ backgroundImage: `url(${leftArrow})` }}
          onClick={() => onArrowClick(false)}
        />
      </div>
      <div ref={rightArrowRef} className={styles.rightArrow}>
        <div
          id={`${SWIMLANE_RIGHT_ARROW + navId}`}
          style={{ backgroundImage: `url(${rightArrow})` }}
          onClick={() => onArrowClick(true)}
        />
      </div>
    </div>
  );
};
Swimlane.propTypes = {
  navId: PropTypes.string,
  isTv: PropTypes.bool,
  title: PropTypes.string,
  items: PropTypes.array,
  emptyItems: PropTypes.array,
  alwaysVisible: PropTypes.bool,
  tileContainerClass: PropTypes.string,
  className: PropTypes.string,
  setFocusChanged: PropTypes.func,
  separatorColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  swimlaneSponsorData: PropTypes.object,
};

export default withFocus(Swimlane);
